import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import nfcConfigManager from '../utils/nfcConfigManager.js';

const ShareModal = ({ isOpen, onClose, nfcId = null, userInfo = null }) => {
  const { t } = useTranslation();
  const [shareData, setShareData] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  useEffect(() => {
    if (isOpen) loadShareData();
  }, [isOpen, nfcId]);

  const loadShareData = async () => {
    try {
      const data = await nfcConfigManager.getShareData(nfcId);
      setShareData(data);
    } catch (error) {
      console.error('Failed to load share data:', error);
    }
  };

  const handleNativeShare = async () => {
    if (!shareData) return;
    if (navigator.share) {
      try {
        setIsSharing(true);
        await navigator.share(shareData);
      } catch (error) {
        if (error.name !== 'AbortError') handleCopyLink();
      } finally {
        setIsSharing(false);
      }
    } else {
      handleCopyLink();
    }
  };

  const handleWhatsAppShare = () => {
    if (!shareData) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(shareData.text)}`, '_blank', 'noopener,noreferrer');
  };

  const handleTelegramShare = () => {
    if (!shareData) return;
    window.open(`https://t.me/share/url?url=${encodeURIComponent(shareData.url)}&text=${encodeURIComponent(shareData.text)}`, '_blank', 'noopener,noreferrer');
  };

  const handleEmailShare = () => {
    if (!shareData) return;
    window.location.href = `mailto:?subject=${encodeURIComponent(shareData.title)}&body=${encodeURIComponent(`${shareData.text}\n\n${shareData.url}`)}`;
  };

  const handleSMSShare = () => {
    if (!shareData) return;
    window.location.href = `sms:?body=${encodeURIComponent(`${shareData.text}\n${shareData.url}`)}`;
  };

  const flashCopied = () => {
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleCopyLink = async () => {
    if (!shareData) return;
    try {
      await navigator.clipboard.writeText(shareData.url);
      flashCopied();
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = shareData.url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        flashCopied();
      } catch (e) {
        console.error('Copy failed:', e);
      }
    }
  };

  const handleCopyText = async () => {
    if (!shareData) return;
    try {
      await navigator.clipboard.writeText(`${shareData.text}\n\n${shareData.url}`);
      flashCopied();
    } catch (e) {
      console.error('Copy failed:', e);
    }
  };

  const generateQRUrl = () =>
    shareData ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(shareData.url)}` : '';

  if (!isOpen || !shareData) return null;

  // ── Styles (app earth-tone palette) ─────────────────────────────────────────
  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(45, 35, 28, 0.55)',
    zIndex: 10010, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    animation: 'fadeIn 0.25s ease',
  };
  const sheet = {
    background: 'linear-gradient(180deg, #ffffff 0%, #faf7f2 100%)',
    width: '100%', maxWidth: '480px', borderRadius: '24px 24px 0 0',
    maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 -6px 28px rgba(0,0,0,0.22)',
    animation: 'slideUpIn 0.3s ease',
  };
  const sectionLabel = {
    fontSize: '11px', fontWeight: 700, color: '#B8A082',
    textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 12px 0',
  };
  const methodBtn = {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: '6px', background: '#f8f6f0', border: '1.5px solid #e8ddd0', borderRadius: '14px',
    padding: '12px 6px', cursor: 'pointer', color: '#8B7355', fontSize: '12px', fontWeight: 600,
    transition: 'all 0.2s ease',
  };
  const copyBtn = (active) => ({
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    padding: '13px', borderRadius: '14px', cursor: 'pointer', fontSize: '13px', fontWeight: 700,
    border: active ? 'none' : '1.5px solid #e8ddd0',
    background: active ? '#7fae7f' : 'white',
    color: active ? 'white' : '#8B7355', transition: 'all 0.2s ease',
  });

  return (
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        {/* Handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 8px' }}>
          <div style={{ width: '40px', height: '4px', background: '#D4C5B0', borderRadius: '999px' }} />
        </div>

        <div style={{ padding: '4px 22px 28px' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
            <h3 style={{ fontFamily: "'Noto Serif', serif", fontSize: '20px', fontWeight: 700, color: '#3d3028', margin: '4px 0 0' }}>
              {t('shareModal.title')}
            </h3>
            <button
              onClick={onClose}
              style={{ background: '#f0ebe4', border: 'none', borderRadius: '50%', width: '32px', height: '32px', fontSize: '18px', color: '#8B7355', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
              aria-label={t('shareModal.done')}
            >×</button>
          </div>

          {/* Hero */}
          <div style={{ textAlign: 'center', padding: '18px 0 8px' }}>
            <div style={{ fontSize: '44px', marginBottom: '8px' }}>🎫</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#8B7355', marginBottom: '6px' }}>
              {t('shareModal.hero')}
            </div>
            <div style={{ fontSize: '13px', color: '#B8A082', lineHeight: 1.5, padding: '0 8px' }}>
              {t('shareModal.subtitle')}
            </div>
            {nfcId && (
              <div style={{ display: 'inline-block', marginTop: '12px', padding: '4px 12px', background: '#f0ebe4', borderRadius: '999px', fontSize: '11px', color: '#B8A082', fontWeight: 600 }}>
                {t('shareModal.nfcId', { id: nfcId.substring(0, 8) })}
              </div>
            )}
          </div>

          {/* Premium benefits */}
          <div style={{ background: 'linear-gradient(135deg, #f6efe2 0%, #efe4d0 100%)', border: '1px solid #e8ddc8', borderRadius: '14px', padding: '14px 16px', margin: '16px 0 12px' }}>
            <div style={sectionLabel}>{t('shareModal.benefitsTitle')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {['benefit1', 'benefit2', 'benefit3', 'benefit4', 'benefit5'].map(k => (
                <div key={k} style={{ fontSize: '13px', color: '#6b5a47', fontWeight: 500 }}>{t(`shareModal.${k}`)}</div>
              ))}
            </div>
          </div>

          {/* Preview message */}
          <div style={{ background: '#f8f6f0', border: '1px solid #f0ebe4', borderRadius: '14px', padding: '14px 16px', margin: '12px 0 20px' }}>
            <div style={sectionLabel}>{t('shareModal.previewTitle')}</div>
            <div style={{ fontSize: '13px', color: '#5a4a3a', lineHeight: 1.5, fontStyle: 'italic' }}>
              “{shareData.text}”
            </div>
          </div>

          {/* Share methods */}
          <div style={sectionLabel}>{t('shareModal.chooseMethod')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '10px', marginBottom: '20px' }}>
            {navigator.share && (
              <button style={methodBtn} onClick={handleNativeShare} disabled={isSharing}>
                <span style={{ fontSize: '24px' }}>📤</span>
                <span>{isSharing ? t('shareModal.sharing') : t('shareModal.share')}</span>
              </button>
            )}
            <button style={methodBtn} onClick={handleWhatsAppShare}>
              <span style={{ fontSize: '24px' }}>💬</span><span>WhatsApp</span>
            </button>
            <button style={methodBtn} onClick={handleTelegramShare}>
              <span style={{ fontSize: '24px' }}>✈️</span><span>Telegram</span>
            </button>
            <button style={methodBtn} onClick={handleEmailShare}>
              <span style={{ fontSize: '24px' }}>📧</span><span>Email</span>
            </button>
            <button style={methodBtn} onClick={handleSMSShare}>
              <span style={{ fontSize: '24px' }}>📱</span><span>SMS</span>
            </button>
          </div>

          {/* Copy */}
          <div style={sectionLabel}>{t('shareModal.orCopy')}</div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '22px' }}>
            <button style={copyBtn(copySuccess)} onClick={handleCopyLink}>
              <span>{copySuccess ? '✅' : '🔗'}</span>
              <span>{copySuccess ? t('shareModal.copied') : t('shareModal.copyLink')}</span>
            </button>
            <button style={copyBtn(copySuccess)} onClick={handleCopyText}>
              <span>{copySuccess ? '✅' : '📝'}</span>
              <span>{copySuccess ? t('shareModal.copied') : t('shareModal.copyMessage')}</span>
            </button>
          </div>

          {/* QR */}
          <div style={sectionLabel}>{t('shareModal.qrTitle')}</div>
          <div style={{ textAlign: 'center', background: 'white', border: '1.5px solid #e8ddd0', borderRadius: '16px', padding: '18px', marginBottom: '22px' }}>
            <img src={generateQRUrl()} alt={t('shareModal.qrAlt')} loading="lazy"
              style={{ width: '180px', height: '180px', borderRadius: '10px' }} />
            <p style={{ fontSize: '11px', color: '#B8A082', margin: '12px 0 0', lineHeight: 1.4 }}>
              {t('shareModal.qrInstruction')}
            </p>
          </div>

          {/* Rules */}
          <div style={{ background: '#f8f6f0', border: '1px solid #f0ebe4', borderRadius: '14px', padding: '14px 16px', marginBottom: '22px' }}>
            <div style={sectionLabel}>{t('shareModal.rulesTitle')}</div>
            <ul style={{ margin: 0, paddingLeft: '18px', color: '#7a6a58', fontSize: '12px', lineHeight: 1.7 }}>
              <li>{t('shareModal.rule1')}</li>
              <li>{t('shareModal.rule2')}</li>
              <li>{t('shareModal.rule3')}</li>
              <li>{t('shareModal.rule4')}</li>
            </ul>
          </div>

          {/* Done */}
          <button onClick={onClose} style={{
            width: '100%', padding: '14px', borderRadius: '16px', border: 'none',
            background: '#C4714A', color: 'white', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
          }}>
            {t('shareModal.done')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
